/**
 * Blank view component.
 * @module components/View/BlankView
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Confirm,
  Table,
  TableRow,
  TableHeader,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { deleteContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

import leftKeySVG from '@plone/volto/icons/left-key.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';
import pencilSVG from '@plone/volto/icons/pencil.svg';

/**
 * Blank view component class.
 * @function BlankView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const BlankView = ({ content }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const parentUrl = flattenToAppURL(content['@id'])
    .split('/')
    .slice(0, -1)
    .join('/');

  const onDelete = async () => {
    await dispatch(deleteContent(flattenToAppURL(content['@id'])));
    setShowDeleteConfirm(false);
    history.push(parentUrl);
  };

  return (
    <>
      <div className="button-toolbar">
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => history.push(parentUrl)}
          aria-label="Back"
        >
          <Icon name={leftKeySVG} size="24px" />
        </button>
        <Link
          className="circular-button float-left"
          to={`${flattenToAppURL(content['@id'])}/edit`}
        >
          <Icon name={pencilSVG} size="24px" />
        </Link>
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => setShowDeleteConfirm(true)}
        >
          <Icon name={deleteSVG} size="24px" />
        </button>
        <div className="button-toolbar-title">Blank</div>
      </div>
      <Confirm
        open={showDeleteConfirm}
        header="Delete blank"
        content={`Are you sure you want to delete "${content.title}"?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete}
      />
      <div className="view-padding">
        <article id="blank">
          <h1 className="content-title">{content.title}</h1>
          <Table celled>
            <TableHeader>
              <TableRow>
                <TableHeaderCell
                  rowSpan="4"
                  textAlign="center"
                  className="general sub rotated"
                >
                  <span className="rotate-label">General</span>
                </TableHeaderCell>
                <TableHeaderCell width="6">Code</TableHeaderCell>
                <TableCell width="6">{content.code}</TableCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableCell>{content.name}</TableCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell>Base</TableHeaderCell>
                <TableCell>{content.base}</TableCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell>Head</TableHeaderCell>
                <TableCell>{content.head}</TableCell>
              </TableRow>
            </TableHeader>
          </Table>
        </article>
      </div>
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
BlankView.propTypes = {
  content: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    base: PropTypes.string,
    head: PropTypes.string,
  }).isRequired,
};

export default BlankView;
